---
title: "Post your first job"
description: "Create a job in Chosen, write the description, set application questions, and publish it to your public careers page. Where the URL comes from."
category: "getting-started"
keywords:
  - "post a job ATS"
  - "create job posting"
  - "publish job careers page"
  - "job application form"
schema: "HowTo"
nav:
  section: "getting-started"
  order: 60
  parent: "/getting-started"
---

A job in Chosen is two things at once: the place candidates collect for that role, and a public posting people can apply to. This page walks the whole thing — create it, write it, publish it.

## Create the job

<Steps>
  <li>Click **Jobs** in the left sidebar, then **New job**.</li>
  <li>Fill in the title, department, and location. The title is what candidates see on your careers page, so write the title, not the requisition code.</li>
  <li>Set the location type — onsite, remote, or hybrid. Candidates filter your careers page on this.</li>
  <li>Save. The job now exists, visible to your team but not yet to the public.</li>
</Steps>

## Write the description

The description field takes formatted text — paste from a doc and it usually comes across cleanly. Keep it readable: candidates skim. The description also feeds the [AI match rating](/guides/candidates), which scores applicants against what the post actually says — so a vague description produces vague scores. Specifics help both the human and the machine reading it.

## Application questions

Every job has a built-in form: name, email, resume. Beyond that you can add your own questions — short text, long answers, multiple choice, file uploads.

Keep the list short. Every extra required question is a few more candidates deciding they'd rather not. Anything you could ask in a phone screen, ask in the phone screen — not on the form.

## Publish it

A new job starts **unpublished**: your team can see it, the public can't.

<Steps>
  <li>Open the job and flip **Published** on.</li>
  <li>The job appears on your careers page and its apply form goes live.</li>
</Steps>

The job is now live at `hire.chosenhq.com/careers/<your-org>/<job-slug>`. Share that link anywhere you'd post a role.

<Callout type="warning" title="Your careers page is off until you turn it on">
Publishing a *job* isn't the same as publishing your *careers page*. The careers page itself is off by default — until you enable it in **Settings → Organization**, even a published job's link won't resolve for the public. Turn the careers page on once, and after that publishing a job is all it takes.
</Callout>

## Where to go next

When applications start arriving, they land in the job's candidate list — [working with candidates](/guides/candidates) is what to read next. To customize how the careers page looks and behaves, see [your careers page](/guides/careers).
